import { useCallback, useEffect, useState } from "react";
import {
  authenticateUser,
  confirmSignUp,
  getResendConfirmationCodeFn,
  SIGN_IN_STATUS,
  KAHUN_PLATFORM,
} from "../cognito/kahunUserAuth";
import { ACTION, CATEGORY, evt } from "fe-common/analytics/analytics";
import { SupportedLanguages } from "@kahunmedical/common-configuration/dist/configuration/supportedLanguages";

interface useLoginProps {
  onSignIn: (email?: string) => void;
  onError?: (error: any) => void;
  disabled?: boolean;
  keepLoading?: boolean;
  onLoading?: () => void;
  onLoadingDone?: () => void;
  platform: KAHUN_PLATFORM;
  clinic?: string;
  locale?: string;
}
export const useLogin = (props: useLoginProps) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [verificationCodeFn, setVerificationCodeFn] = useState(null);
  const [verificationCode, setVerificationCode] = useState("");
  const [loading, setLoadingState] = useState(false);
  //const passwordRef = useRef<HTMLInputElement>();

  const {
    keepLoading,
    onLoading,
    onLoadingDone,
    onSignIn,
    clinic = null,
    locale = SupportedLanguages.English_US,
    platform = KAHUN_PLATFORM.UNKNOWN,
    onError = console.error,
  } = props;
  /* Sync controlled props with sate */
  useEffect(() => {
    setLoadingState(keepLoading);
  }, [keepLoading, setLoadingState]);

  /* Keep loading state synced with parent if needed */
  const setLoading = useCallback(
    (state: boolean) => {
      if (state && onLoading) return onLoading();
      if (!state && onLoadingDone) return onLoadingDone();
      // Let parent update the state if we are controlled
      if (keepLoading || keepLoading === false) return;
      setLoadingState(state);
    },
    [onLoading, onLoadingDone, keepLoading, setLoadingState],
  );

  const handleSignIn = useCallback(
    (e) => {
      if (!email || !password) return;
      setLoading(true);
      e.preventDefault();
      (async () => {
        try {
          await authenticateUser(email, password);
          evt({
            category: CATEGORY.USER_AUTHENTICATION,
            action: ACTION.LOG_IN,
            props: {
              platform,
              userId: email,
              clinic,
              locale,
            },
          });
          onSignIn && onSignIn(email);
        } catch (err) {
          if (err === SIGN_IN_STATUS.CONFIRM_SIGN_IN_MISSING) {
            onError(
              "User exists, but not verified. Use the verification code sent to your email",
            );
            const verificationFn = getResendConfirmationCodeFn(email);
            setVerificationCodeFn(verificationFn);
            (await verificationFn)();
          } else {
            onError(err);
          }
        } finally {
          setLoading(false);
        }
      })();
    },
    [email, password, onSignIn, onError, setLoading],
  );

  const handleVerify = useCallback(
    (e) => {
      e.preventDefault();
      (async () => {
        try {
          await confirmSignUp(email, verificationCode);
          evt({
            category: CATEGORY.USER_AUTHENTICATION,
            action: ACTION.SIGN_UP,
            props: {
              platform,
              userId: email,
              clinic,
              locale,
            },
          });
          setVerificationCodeFn(null);
          handleSignIn(e);
        } catch (e) {
          onError((e as Error).message || e);
        }
      })();
    },
    [handleSignIn, verificationCode, email, onError],
  );

  return {
    setEmail,
    setPassword,
    setVerificationCode,
    verificationCodeFn,
    loading,
    handleVerify,
    handleSignIn,
  };
};
